import React, { Suspense, useEffect, useRef } from 'react'

import { parseAsBoolean, useQueryState } from 'nuqs'
import { Redirect, Route, Switch, useLocation, useHistory } from 'react-router-dom'

import { UserAuthorizationResultsSchemaRedirectTo } from '@/api/types'
import { LoadingScreen } from '@/components/ui/loading-screen'
import { PAGES } from '@/lib/pages'
import { authService } from '@/services/auth-service'
import { sessionStorageService } from '@/services/storage-service'
import { useAppState } from '@/states/app-state'

import SuperAdminRoutes from '../pages/internal/super_admin/SuperAdminRoutes'
import { CpaRoutes } from './CpaRoutes'
import { EssRoutes } from './EssRoutes'
import PayrollRoutes from './PayrollRoutes'
import ProtectedRoute from './ProtectedRoute'

/**
 * Containers/Components (do not render a route)
 */
const LayoutMain = React.lazy(() => import('../containers/LayoutMain/LayoutMain'))
const EmployeeForm = React.lazy(() => import('../containers/EmployeeForm'))

/**
 * Auth routes -> /auth
 */
const PageBrandsLogin = React.lazy(() => import('../v2/pages/auth/login/page'))
const PageBrands2FA = React.lazy(() => import('../v2/pages/auth/login/mfa/page'))
const PageBrands2FAPrompt = React.lazy(() => import('../v2/pages/auth/login/security/page'))
const PageBrands2FAAuthenticator = React.lazy(() => import('../v2/pages/auth/login/authenticator/page'))
const PageBrands2FARecovery = React.lazy(() => import('../v2/pages/auth/login/recovery/page'))
const PageResetPasswordVerify = React.lazy(() => import('../v2/pages/auth/reset-password/verify/page'))
const PageResetPasswordSent = React.lazy(() => import('../v2/pages/auth/reset-password/sent/page'))
const PageConfirmEmail = React.lazy(() => import('../v2/pages/auth/reset-password/email/page'))
const PageConfirmPassword = React.lazy(() => import('../v2/pages/auth/set-up-account/password/page'))
const PageFlowComplete = React.lazy(() => import('../v2/pages/auth/set-up-account/complete/page'))
const PageSelectCompanyContainer = React.lazy(() => import('../v2/pages/auth/select-company/page'))
const Impersonate = React.lazy(() => import('../v2/pages/auth/logout/[guid]/page'))

/**
 * Dashboard routes -> /dashboard
 */
const PageDashboard = React.lazy(() => import('../pages/dashboard'))
/**
 * Reports routes -> /reports
 */
const PageReports = React.lazy(() => import('../pages/reports'))
const PageReportsQB = React.lazy(() => import('../pages/reports/qb'))
const PageReportsSectionPosted = React.lazy(() => import('../pages/reports/posted'))
const PageReportsSection = React.lazy(() => import('../pages/reports/[sectionId]'))
const PageReportsRunV2 = React.lazy(() => import('../pages/reports/run-v2'))
const PageReportsRunV2Section = React.lazy(() => import('../pages/reports/run-v2/[sectionId]'))
/**
 * Settings routes -> /settings
 */
const SettingsMain = React.lazy(() => import('../pages/settings'))
const SettingsUsers = React.lazy(() => import('../pages/settings/users'))
const SettingsManageCPAPortal = React.lazy(() => import('../pages/settings/cpaportal'))
const SettingsNotifications = React.lazy(() => import('../pages/settings/notifications'))
const SupportTickets = React.lazy(() => import('../pages/support/PageZendeskTickets'))
const SupportTicketDetail = React.lazy(() => import('../pages/support/PageZendeskTicketDetail'))
const PageEmployeeDeckSettings = React.lazy(() => import('../pages/settings/employeedeck/PageEmployeeDeckSettings'))
/**
 * Onboarding routes -> /onboarding
 */
const PageOnboarding = React.lazy(() => import('../pages/onboarding/[step]'))
const PageOnboardingNew = React.lazy(() => import('../pages/onboarding/new'))
const PageOnboardingSuccess = React.lazy(() => import('../pages/onboarding/success'))

/**
 * Custom reports routes -> /custom-reports
 */
const PageCustomReports = React.lazy(() => import('../v2/pages/custom-reports/page'))
const PageCustomReport = React.lazy(() => import('../v2/pages/custom-reports/[id]/page'))

/**
 * Employee Onboarding Flows routes -> /employee-onboarding-flows
 */

const PageEmployeeOnboardingFlowList = React.lazy(() => import('../v2/routes/onboarding-flows.index'))
const PageEmployeeOnboardingFlowEdit = React.lazy(() => import('../v2/routes/onboarding-flows.$id'))

/**
 * Onboarding Review routes -> /onboarding-review
 */

const PageOnboardingReviewList = React.lazy(() => import('../v2/routes/onboarding-review.index'))
const PageOnboardingReview = React.lazy(() => import('../v2/routes/onboarding-review.$id'))

/**
 * Employee Onboarding Custom Fields routes -> /employee-onboarding-custom-fields
 */

const PageEmployeeOnboardingCustomFields = React.lazy(
  () => import('../v2/pages/employee-onboarding-custom-fields/page'),
)

/**
 * Document routes -> /document-types
 */

const PageDocumentTypeList = React.lazy(() => import('../v2/routes/documents.types.index'))

/**
 * HR Documents route -> /documents
 */

const PageHrDocuments = React.lazy(() => import('../v2/routes/documents.index'))

/**
 * Onboarding Welcome Screen route -> /onboarding-welcome-screen
 */

const PageOnboardingWelcomeScreen = React.lazy(() => import('../v2/pages/onboarding-welcome-screen/page'))

/**
 * Other Routes
 */
const PageRestricted = React.lazy(() => import('../pages/restricted'))
const TestReactErrorBoundaries = React.lazy(() => import('../pages/test_react_error_boundaries'))
const AuthorizePayroll = React.lazy(() => import('../pages/authorize-payroll'))
const DebugView = React.lazy(() => import('../pages/debug'))
const PagePayrollFinancing = React.lazy(() => import('../pages/payroll-financing'))
const PageEmployees = React.lazy(() => import('../pages/employees'))
const PageNewEmployee = React.lazy(() => import('../pages/employees-new'))
const PageMultiCompanyUsers = React.lazy(() => import('../pages/usersbycompany'))
const PageServices = React.lazy(() => import('../pages/services'))
const PageEmployeeOnboarding = React.lazy(() => import('../pages/employee-onboarding'))
const PageDesignGuidelines = React.lazy(() => import('../pages/design-guidelines'))

const RootDiv = (props) => {
  const location = useLocation()
  const { children, ...rest } = props

  return (
    <div id="routes-root-div" {...rest} className={location.pathname !== '/login' ? 'App mainBody' : 'App'}>
      {children}
    </div>
  )
}

const Routes = (props) => {
  const profile = useAppState((state) => state.profile)
  const initiated = useAppState((state) => state.initiated)
  const permissions = profile ? profile.permission : {}
  const conum = profile?.conum
  const selectedCpaFirmId = profile?.selectedCpaFirmId
  const history = useHistory()
  const { pathname } = useLocation()
  const [isNative] = useQueryState('native', parseAsBoolean.withDefault(false))
  const intervalId = useRef(null)

  // A half-finished login token is only good for the /auth pages; drop it when the user leaves them.
  useEffect(() => {
    if (!pathname.startsWith('/auth')) authService.clearIntermediateToken()
  }, [pathname])

  useEffect(() => {
    try {
      const { conum: sessionConum, empnum } = sessionStorageService.getEmployeeId() ?? {}
      if (empnum && sessionConum && conum && sessionConum === conum) {
        sessionStorageService.removeEmployeeId()
        history.push(`/employees/${empnum}/employeePersonalInfo`)
      }
    } catch (err) {
      console.log(err)
    }
  }, [conum])

  useEffect(() => {
    if (isNative) {
      sessionStorageService.setIsNative()
    }
  }, [isNative])

  useEffect(() => {
    intervalId.current = setInterval(() => {
      const messagingIframe = document.querySelector('iframe[name="Messaging window"]')
      if (messagingIframe) {
        if (messagingIframe.style.height !== '0px') {
          const regex = /^#\d+ ~.*$/
          const regexToReplace = /^#\d+ ~/
          const url = new URL(window.location)
          url.hash = `#conum=${conum}`
          if (regex.test(document.title)) {
            document.title = document.title.replace(regexToReplace, `#${conum} ~`)
          } else {
            document.title = `#${conum} ~ ${document.title}`
          }
          window.history.pushState({ state: 'new' }, document.title, url)
        }
      }
    }, 1000)
    return () => {
      clearInterval(intervalId.current)
    }
  }, [conum])

  if (!initiated) return <LoadingScreen show />

  return (
    <RootDiv>
      <Suspense {...props} fallback={<LoadingScreen show />}>
        <Switch>
          <Route exact path={PAGES.reportsPrimary.path}>
            <Redirect to={PAGES.reports.path} />
          </Route>
          <Route exact path="/debug" component={() => <DebugView />} />
          <Route path="/internal/super_admin" component={() => <SuperAdminRoutes />} />
          {/* <SuperAdminLayout /> */}

          <Route exact path="/" component={() => <Redirect to={PAGES.brandsLogin.path} />} />

          <Route exact path={`${PAGES.impersonate.path}/:guid`} component={Impersonate} />
          <Route exact path={PAGES.twoFactorAuthLogin.path} component={PageBrands2FA} />
          <Route exact path={PAGES.twoFactorAuthPrompt.path} component={PageBrands2FAPrompt} />
          <Route exact path={PAGES.twoFactorAuthAuthenticator.path} component={PageBrands2FAAuthenticator} />
          <Route exact path={PAGES.twoFactorAuthRecovery.path} component={PageBrands2FARecovery} />
          <Route exact path={PAGES.resetPasswordVerify.path} component={PageResetPasswordVerify} />
          <Route exact path={PAGES.resetPasswordSent.path} component={PageResetPasswordSent} />
          <Route path={PAGES.brandsLogin.path} component={PageBrandsLogin} />
          <Route path={`${PAGES.onboarding.path}/new`} component={PageOnboardingNew} />
          <Route path={`${PAGES.onboarding.path}/success`} component={PageOnboardingSuccess} />
          <Route exact path={PAGES.brandsLoginSelectCompany.path} component={PageSelectCompanyContainer} />
          <Route exact path={PAGES.setUpAccountPassword.path} component={PageConfirmPassword} />
          <Route exact path={PAGES.resetPasswordEmail.path} component={PageConfirmEmail} />
          <Route exact path={PAGES.resetPasswordInput.path} component={PageConfirmPassword} />
          <Route exact path={PAGES.resetPasswordComplete.path} component={PageFlowComplete} />
          <Route exact path={PAGES.testReactErrorBoundaries.path} component={TestReactErrorBoundaries} />
          <ProtectedRoute
            exact
            path={PAGES.restrictedIp.path}
            userOnlyAuthenticated
            permission
            component={PageRestricted}
          />
          <Route
            exact
            path={`${PAGES.employeeOnboarding.path}/:onboardingCode`}
            render={(props) => <PageEmployeeOnboarding {...props} />}
          />
          <Route exact path={PAGES.authorizePayroll.path} component={AuthorizePayroll} />
          <Redirect path={PAGES.standardReportsOld.path} to={PAGES.reportsPrimary.path} />
          <Redirect path={PAGES.runReportOld.path} to={PAGES.reportsRunV2.path} />
          <Redirect path={PAGES.reportsRun.path} to={PAGES.reportsRunV2.path} />
          <Redirect path={PAGES.myReportsOld.path} to={PAGES.reportsPosted.path} />
          <Route exact path={PAGES.designGuidelines.path} component={PageDesignGuidelines} />

          <LayoutMain>
            <Suspense {...props} fallback={<LoadingScreen show />}>
              <Switch>
                <ProtectedRoute
                  path={PAGES.dashboard.path}
                  permission={permissions?.dashboard}
                  component={PageDashboard}
                  disabled={!conum}
                />
                <ProtectedRoute
                  exact
                  path={PAGES.platinumPayExpress.path}
                  title={PAGES.platinumPayExpress.title}
                  permission={permissions?.managePayroll}
                  disabled={!conum}
                />
                <ProtectedRoute
                  exact
                  path={PAGES.timeKeeping.path}
                  title={PAGES.timeKeeping.title}
                  permission={!!(profile?.swipeClockLoginName || profile?.wfmLink)}
                  disabled={!conum}
                />
                <ProtectedRoute
                  exact
                  path={PAGES.hrNextDirect.path}
                  title={PAGES.hrNextDirect.title}
                  permission={permissions?.managePayroll}
                  disabled={!conum}
                />
                <ProtectedRoute
                  path={PAGES.payrollFinancing.path}
                  title={PAGES.payrollFinancing.title}
                  permission={permissions?.payrollFunding}
                  component={PagePayrollFinancing}
                  disabled={!conum}
                />
                <ProtectedRoute
                  path={`${PAGES.employees.path}/:empnum/:section`}
                  title={PAGES.employeeForm.title}
                  permission={permissions?.employee}
                  render={(props) => {
                    return (
                      <>
                        <EmployeeForm {...props} />
                        <PageEmployees {...props} />
                      </>
                    )
                  }}
                  disabled={!conum}
                />
                <ProtectedRoute
                  path={`${PAGES.employees.path}/:empnum`}
                  title={PAGES.employeeForm.title}
                  permission={permissions?.employee}
                  render={(props) => {
                    return (
                      <>
                        <EmployeeForm {...props} />
                        <PageEmployees {...props} />
                      </>
                    )
                  }}
                  disabled={!conum}
                />
                <ProtectedRoute
                  path={PAGES.employees.path}
                  title={PAGES.employees.title}
                  permission={permissions?.employee}
                  render={(props) => {
                    return (
                      <>
                        <EmployeeForm {...props} />
                        <PageEmployees {...props} />
                      </>
                    )
                  }}
                  disabled={!conum}
                />
                <ProtectedRoute
                  path={PAGES.reportsRunV2.path}
                  title={PAGES.reportsRunV2.title}
                  permission={permissions?.runReports}
                  component={PageReportsRunV2}
                  disabled={!conum}
                />
                <ProtectedRoute
                  path={PAGES.reportsPosted.path}
                  title={PAGES.reportsPosted.title}
                  permission
                  component={PageReportsSectionPosted}
                  disabled={!conum}
                />
                <ProtectedRoute
                  path={`${PAGES.reports.path}/qb`}
                  title={PAGES.reports.title}
                  permission
                  component={PageReportsQB}
                />
                <ProtectedRoute
                  path={`${PAGES.reports.path}/:sectionId`}
                  title={PAGES.reports.title}
                  permission
                  component={PageReportsSection}
                  disabled={!conum}
                />
                <ProtectedRoute
                  path={PAGES.reports.path}
                  title={PAGES.reports.title}
                  permission
                  component={PageReports}
                  disabled={!conum}
                />
                <ProtectedRoute
                  path={PAGES.employeeDeckSettings.path}
                  title={PAGES.employeeDeckSettings.title}
                  permission={permissions?.manageEmployeeDeck}
                  component={PageEmployeeDeckSettings}
                  disabled={!conum}
                />
                <ProtectedRoute
                  path={PAGES.settingsMain.path}
                  title={PAGES.settingsMain.title}
                  permission={permissions?.manageContactsAndUsers}
                  component={SettingsMain}
                  exact
                  disabled={!conum}
                />

                <ProtectedRoute
                  path={PAGES.settingsUsers.path}
                  title={PAGES.settingsUsers.title}
                  permission={permissions?.manageContactsAndUsers}
                  component={SettingsUsers}
                  disabled={!conum}
                />
                <ProtectedRoute
                  path={PAGES.settingsManageCpaPortal.path}
                  title={PAGES.settingsManageCpaPortal.title}
                  permission={permissions?.manageContactsAndUsers}
                  component={SettingsManageCPAPortal}
                  disabled={!conum}
                />
                <ProtectedRoute
                  path={PAGES.settingsNotifications.path}
                  title={PAGES.settingsNotifications.title}
                  permission
                  component={SettingsNotifications}
                  disabled={!conum}
                />
                <ProtectedRoute
                  path={PAGES.supportTicketDetail.path}
                  title={PAGES.supportTicketDetail.title}
                  permission
                  component={SupportTicketDetail}
                  disabled={!conum}
                />
                <ProtectedRoute
                  path={PAGES.supportTickets.path}
                  title={PAGES.supportTickets.title}
                  permission
                  component={SupportTickets}
                  disabled={!conum}
                />
                <ProtectedRoute
                  path={PAGES.services.path}
                  title={PAGES.services.title}
                  permission={permissions?.services}
                  component={PageServices}
                  disabled={!conum}
                />
                <ProtectedRoute
                  path={`${PAGES.reportsRunV2.path}/:sectionId`}
                  title={PAGES.reportsRunV2.title}
                  permission={permissions?.runReports}
                  component={PageReportsRunV2Section}
                  disabled={!conum}
                />
                <ProtectedRoute
                  path={PAGES.newEmployees.path}
                  title={PAGES.newEmployees.title}
                  permission={permissions?.employee}
                  component={PageNewEmployee}
                  disabled={!conum}
                />
                <ProtectedRoute
                  path={`${PAGES.employeesOnboarding.path}/:id`}
                  title={PAGES.employeesOnboarding.title}
                  permission={permissions?.employee}
                  component={PageOnboardingReview}
                  disabled={!conum}
                />
                <ProtectedRoute
                  path={PAGES.employeesOnboarding.path}
                  title={PAGES.employeesOnboarding.title}
                  permission={permissions?.employee}
                  component={PageOnboardingReviewList}
                  disabled={!conum}
                />
                <ProtectedRoute
                  path={`${PAGES.employeeOnboardingFlows.path}/:id`}
                  title={PAGES.employeeOnboardingFlows.title}
                  permission={permissions?.employee}
                  component={PageEmployeeOnboardingFlowEdit}
                  disabled={!conum}
                />
                <ProtectedRoute
                  path={PAGES.employeeOnboardingFlows.path}
                  title={PAGES.employeeOnboardingFlows.title}
                  permission={permissions?.employee}
                  component={PageEmployeeOnboardingFlowList}
                  disabled={!conum}
                />
                <ProtectedRoute
                  path={PAGES.employeeOnboardingCustomFields.path}
                  title={PAGES.employeeOnboardingCustomFields.title}
                  permission={permissions?.employee}
                  component={PageEmployeeOnboardingCustomFields}
                  disabled={!conum}
                />
                <ProtectedRoute
                  path={PAGES.documentTypes.path}
                  title={PAGES.documentTypes.title}
                  permission={permissions?.employee}
                  component={PageDocumentTypeList}
                  disabled={!conum}
                />
                <ProtectedRoute
                  path={PAGES.hrDocuments.path}
                  title={PAGES.hrDocuments.title}
                  permission={permissions?.employee}
                  component={PageHrDocuments}
                  disabled={!conum}
                />
                <ProtectedRoute
                  path={PAGES.onboardingWelcomeScreen.path}
                  title={PAGES.onboardingWelcomeScreen.title}
                  permission={permissions?.employee}
                  component={PageOnboardingWelcomeScreen}
                  disabled={!conum}
                />
                <ProtectedRoute
                  path={PAGES.usersbyCompany.path}
                  title={PAGES.usersbyCompany.title}
                  permission={permissions?.manageContactsAndUsers}
                  component={PageMultiCompanyUsers}
                  disabled={!conum}
                />
                <ProtectedRoute
                  path={`${PAGES.onboarding.path}/:step/:empnum/:section`}
                  title={PAGES.onboarding.title}
                  permission
                  component={PageOnboarding}
                  disabled={!conum}
                />
                <ProtectedRoute
                  path={`${PAGES.onboarding.path}/:step`}
                  title={PAGES.onboarding.title}
                  permission={profile?.redirectTo === UserAuthorizationResultsSchemaRedirectTo.Onboarding}
                  component={PageOnboarding}
                  disabled={!conum}
                />
                <ProtectedRoute
                  path={`${PAGES.customReports.path}/:id`}
                  title={PAGES.customReports.title}
                  permission={permissions?.customReports}
                  component={PageCustomReport}
                  disabled={!conum}
                />
                <ProtectedRoute
                  path={PAGES.customReports.path}
                  title={PAGES.customReports.title}
                  permission={permissions?.customReports}
                  component={PageCustomReports}
                  disabled={!conum}
                />
                <Route path="/payrolls" render={() => <PayrollRoutes permissions={permissions} />} />
                <Route
                  path="/cpa"
                  render={() => <CpaRoutes selectedCpaFirmId={selectedCpaFirmId} permission={permissions} />}
                />
                <Route path="/ess" render={() => <EssRoutes />} />

                <Redirect to={PAGES.dashboard.path} />
              </Switch>
            </Suspense>
          </LayoutMain>
        </Switch>
      </Suspense>
    </RootDiv>
  )
}

export default Routes
